/**
 * TokenRefreshManager - Handles waiting for token refresh on 401 errors
 *
 * When a 401 error occurs, instead of immediately showing an error,
 * this manager waits for a new token from the parent iframe.
 * If a token arrives within the timeout, the request can be retried.
 */

/** Custom error thrown when token is refreshed and request should be retried */
export class TokenRefreshError extends Error {
  constructor(message: string = 'Token refreshed - retry request') {
    super(message);
    this.name = 'TokenRefreshError';
  }
}

export class TokenRefreshManager {
  private waitingPromise: Promise<string> | null = null;
  private resolveWaiting: ((token: string) => void) | null = null;
  private rejectWaiting: ((error: Error) => void) | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Wait for a new token from parent iframe
   * @param timeoutMs - How long to wait before timing out (default 5000ms)
   * @returns Promise that resolves with the new token or rejects on timeout
   */
  async waitForToken(timeoutMs: number = 5000): Promise<string> {
    // If already waiting, return existing promise (multiple 401s share same wait)
    if (this.waitingPromise) {
      console.log('⏳ Already waiting for token refresh, joining existing wait...');
      return this.waitingPromise;
    }

    console.log(`⏳ 401 received - waiting up to ${timeoutMs}ms for new token from parent...`);

    this.waitingPromise = new Promise<string>((resolve, reject) => {
      this.resolveWaiting = resolve;
      this.rejectWaiting = reject;

      // Set timeout
      this.timeoutId = setTimeout(() => {
        console.log('⏰ Token refresh timeout - no new token received');
        if (this.rejectWaiting) {
          this.rejectWaiting(new Error('Token refresh timeout'));
        }
        this.cleanup();
      }, timeoutMs);
    });

    return this.waitingPromise;
  }

  /**
   * Called when a new token is received via postMessage
   * Resolves any pending waitForToken promises
   */
  notifyTokenReceived(token: string): void {
    if (this.resolveWaiting) {
      console.log('✅ New token received while waiting - resolving promise');

      // Clear timeout since we got the token
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }

      this.resolveWaiting(token);
      this.cleanup();
    }
  }

  /**
   * Check if currently waiting for a token refresh
   */
  isWaiting(): boolean {
    return this.waitingPromise !== null;
  }

  /**
   * Cancel any pending wait (useful for cleanup)
   */
  cancel(): void {
    if (this.rejectWaiting) {
      this.rejectWaiting(new Error('Token refresh cancelled'));
    }
    this.cleanup();
  }

  private cleanup(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.waitingPromise = null;
    this.resolveWaiting = null;
    this.rejectWaiting = null;
  }
}

/** Singleton instance */
export const tokenRefreshManager = new TokenRefreshManager();
